'use client';

import Image, { getImageProps, type StaticImageData } from 'next/image';
import type { CSSProperties } from 'react';

type BannerSource = StaticImageData | string;

interface ResponsiveBannerProps {
  alt: string;
  desktopSrc: BannerSource;
  tabletSrc?: BannerSource;
  mobileSrc?: BannerSource;
  priority?: boolean;
  sizes?: string;
  className?: string;
  imageClassName?: string;
  backgroundColor?: string;
  maxWidth?: number;
  height?: string | number;
  forcedAspectRatio?: number;
  desktopBreakpoint?: number;
  tabletBreakpoint?: number;
}

const DEFAULT_SIZES = '(max-width: 767px) 100vw, (max-width: 1199px) 100vw, 1536px';

function getSourceWidth(source: BannerSource) {
  return typeof source === 'string' ? undefined : source.width;
}

function getSourceHeight(source: BannerSource) {
  return typeof source === 'string' ? undefined : source.height;
}

function getSourceAspectRatio(source: BannerSource) {
  const width = getSourceWidth(source);
  const height = getSourceHeight(source);

  return width && height ? width / height : undefined;
}

function getOptimizedSourceSet(source: BannerSource, alt: string, sizes: string) {
  const { props } = getImageProps({
    src: source,
    alt,
    width: getSourceWidth(source) || 1536,
    height: getSourceHeight(source) || 300,
    sizes,
    quality: 75,
  });

  return props.srcSet;
}

export default function ResponsiveBanner({
  alt,
  desktopSrc,
  tabletSrc,
  mobileSrc,
  priority = false,
  sizes = DEFAULT_SIZES,
  className = '',
  imageClassName = '',
  backgroundColor = '#f5eee0',
  maxWidth,
  height,
  forcedAspectRatio,
  desktopBreakpoint = 1200,
  tabletBreakpoint = 768,
}: ResponsiveBannerProps) {
  const fallbackTabletSrc = tabletSrc || desktopSrc;
  const fallbackMobileSrc = mobileSrc || fallbackTabletSrc;

  const desktopAspectRatio = forcedAspectRatio || getSourceAspectRatio(desktopSrc) || 1.7;
  const tabletAspectRatio =
    forcedAspectRatio || getSourceAspectRatio(fallbackTabletSrc) || desktopAspectRatio;
  const mobileAspectRatio =
    forcedAspectRatio || getSourceAspectRatio(fallbackMobileSrc) || tabletAspectRatio;
  const tabletSrcSet = getOptimizedSourceSet(fallbackTabletSrc, alt, sizes);
  const mobileSrcSet = getOptimizedSourceSet(fallbackMobileSrc, alt, sizes);

  const shellStyle = {
    backgroundColor,
    maxWidth: maxWidth ? `${maxWidth}px` : undefined,
    height: height ?? undefined,
    '--banner-desktop-ratio': `${desktopAspectRatio}`,
    '--banner-tablet-ratio': `${tabletAspectRatio}`,
    '--banner-mobile-ratio': `${mobileAspectRatio}`,
  } as CSSProperties & Record<string, string | number | undefined>;

  return (
    <section
      className={`responsive-banner ${className}`.trim()}
      style={shellStyle}
      data-fixed-height={height ? 'true' : 'false'}
    >
      <picture>
        <source
          media={`(max-width: ${tabletBreakpoint - 1}px)`}
          srcSet={mobileSrcSet}
          sizes={sizes}
        />
        <source
          media={`(max-width: ${desktopBreakpoint - 1}px)`}
          srcSet={tabletSrcSet}
          sizes={sizes}
        />
        <Image
          src={desktopSrc}
          alt={alt}
          fill
          sizes={sizes}
          preload={priority}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          className={`responsive-banner-image ${imageClassName}`.trim()}
          style={{ objectFit: 'cover' }}
        />
      </picture>

      <style jsx>{`
        .responsive-banner {
          position: relative;
          width: 100%;
          overflow: hidden;
          margin: 0 auto;
        }

        .responsive-banner[data-fixed-height='false'] {
          aspect-ratio: var(--banner-desktop-ratio);
        }

        @media (max-width: 1199px) {
          .responsive-banner[data-fixed-height='false'] {
            aspect-ratio: var(--banner-tablet-ratio, var(--banner-desktop-ratio));
          }
        }

        @media (max-width: 767px) {
          .responsive-banner[data-fixed-height='false'] {
            aspect-ratio: var(
              --banner-mobile-ratio,
              var(--banner-tablet-ratio, var(--banner-desktop-ratio))
            );
          }
        }

        .responsive-banner :global(picture) {
          display: block;
          width: 100%;
          height: 100%;
        }
      `}</style>
    </section>
  );
}
